import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
  Button,
  Check,
  ChevronDown,
  Copy01,
  Download01,
  LinkExternal01,
  XClose
} from '@opendate-app/setlist';

import { LogoLockup } from './ExistingAiPanelLogos';

export const existingAiIntegrationPropType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  serverUrl: PropTypes.string.isRequired,
  providers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    badge: PropTypes.string,
    summary: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    copyBlocks: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })).isRequired,
    link: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    }),
    download: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    }),
    quickActionsTitle: PropTypes.string,
    quickActionsDescription: PropTypes.string,
    manualInstructionsTitle: PropTypes.string
  })).isRequired
});

const PANEL_STYLE = {
  border: '1px solid var(--od-border-2, #E9EAEB)',
  borderRadius: '12px',
  backgroundColor: 'var(--od-surface, #FFFFFF)',
  overflow: 'hidden',
  boxShadow: '0 1px 2px 0 rgba(10, 13, 18, 0.05)'
};

const BADGE_BUTTON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--od-accent, #136395)',
  backgroundColor: 'var(--od-accent-subtle, #E4F2FC)',
  border: '1px solid var(--od-accent-border, #B2DBF5)',
  borderRadius: '9999px',
  padding: '3px 8px 3px 10px',
  cursor: 'pointer',
  lineHeight: '1.3'
};

const STEPS_CARD_STYLE = {
  background: 'var(--od-surface-2, #F5F5F5)',
  border: '1px solid var(--od-border-2, #E9EAEB)',
  borderRadius: '10px',
  padding: '14px'
};

const STEP_NUMBER_STYLE = {
  width: '20px',
  height: '20px',
  minWidth: '20px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#FFFFFF',
  backgroundColor: '#1982C4',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '10px',
  marginTop: '1px'
};

const CODE_BLOCK_STYLE = {
  padding: '10px 12px',
  fontSize: '12px',
  lineHeight: '1.5',
  color: 'var(--od-text, #333333)',
  backgroundColor: 'var(--od-surface-2, #F5F5F5)',
  border: '1px solid var(--od-border-2, #E9EAEB)',
  borderRadius: '8px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all'
};

const QUICK_ACTION_STYLE = {
  alignItems: 'center',
  borderRadius: '9px',
  display: 'flex',
  fontSize: '13px',
  fontWeight: 600,
  gap: '8px',
  justifyContent: 'center',
  minHeight: '40px',
  padding: '9px 14px',
  textDecoration: 'none',
  width: '100%'
};

const CopyButton = ({ label, value, onCopy }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(value, `${label} copied`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="secondary" size="sm" icon={copied ? Check : Copy01} onClick={handleCopy}>
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

CopyButton.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired
};

const ExistingAiPanel = ({ existingAiIntegration, onCopy, onDismiss, onSetup, isMobileLayout, variant }) => {
  const providers = existingAiIntegration?.providers || [];
  const [selectedProviderId, setSelectedProviderId] = useState(providers[0]?.id);
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedProvider = providers.find((provider) => provider.id === selectedProviderId) || providers[0];

  if (!existingAiIntegration || !selectedProvider) {
    return null;
  }

  const isCard = variant === 'card';

  return (
    <div className={isCard ? 'existing-ai-panel-card' : undefined} style={isCard ? undefined : PANEL_STYLE}>
      {isCard ? (
        <div className="existing-ai-panel-card-summary">
          <div className="existing-ai-panel-card-tiles" aria-hidden="true">
            <span className="existing-ai-panel-card-tile is-claude"><img src="/conductor/connect-claude.svg" alt="" /></span>
            <span className="existing-ai-panel-card-tile is-conductor"><img src="/conductor/connect-conductor.svg" alt="" /></span>
            <span className="existing-ai-panel-card-tile is-chatgpt"><img src="/conductor/connect-chatgpt.svg" alt="" /></span>
          </div>
          <div className="existing-ai-panel-card-copy">
            <strong>{existingAiIntegration.title}</strong>
            {existingAiIntegration.subtitle && <p>{existingAiIntegration.subtitle}</p>}
          </div>
          <button
            type="button"
            className="existing-ai-panel-card-setup"
            aria-expanded={onSetup ? undefined : isExpanded}
            onClick={onSetup || (() => setIsExpanded(!isExpanded))}
          >
            {selectedProvider.badge}
          </button>
          {onDismiss && (
            <Button variant="tertiary" size="sm" icon={XClose} onClick={onDismiss} className="existing-ai-panel-card-dismiss" aria-label="Dismiss" />
          )}
        </div>
      ) : (
        <div
          style={{
            padding: isMobileLayout ? '8px 10px' : '12px 16px',
            borderBottom: isExpanded ? '1px solid var(--od-border-2, #E9EAEB)' : 'none',
            background: 'var(--od-surface-3, #FAFAFA)'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center" style={{ gap: isMobileLayout ? '8px' : '12px', minWidth: 0 }}>
              <LogoLockup />
              <div style={{ minWidth: 0 }}>
                <p className="mb-0" style={{
                  fontSize: isMobileLayout ? '13px' : '14px',
                  fontWeight: 600,
                  color: 'var(--od-text, #333333)',
                  lineHeight: '1.3',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {existingAiIntegration.title}
                </p>
                {!isMobileLayout && (
                  <p className="mb-0" style={{ fontSize: '12px', color: 'var(--od-muted, #717680)', lineHeight: '1.4', marginTop: '1px' }}>
                    {existingAiIntegration.subtitle}
                  </p>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center" style={{ gap: isMobileLayout ? '4px' : '8px', flexShrink: 0 }}>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  ...BADGE_BUTTON_STYLE,
                  padding: isMobileLayout ? '3px 6px 3px 8px' : BADGE_BUTTON_STYLE.padding,
                  fontSize: isMobileLayout ? '11px' : BADGE_BUTTON_STYLE.fontSize
                }}
              >
                {selectedProvider.badge}
                <ChevronDown
                  width={14}
                  height={14}
                  aria-hidden="true"
                  style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
                />
              </button>
              {onDismiss && (
                <Button variant="tertiary" size="sm" icon={XClose} onClick={onDismiss} aria-label="Dismiss" />
              )}
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div style={{ padding: isMobileLayout ? '12px' : '16px' }}>
          {providers.length > 1 && (
            <div className="mb-3 d-flex flex-wrap" style={{ gap: '6px' }}>
              {providers.map((provider) => (
                <Button
                  key={provider.id}
                  variant={selectedProvider.id === provider.id ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setSelectedProviderId(provider.id)}
                >
                  {provider.name}
                </Button>
              ))}
            </div>
          )}

          {selectedProvider.quickActionsTitle && selectedProvider.link && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: 'var(--od-text, #333333)', fontSize: '14px', fontWeight: 600, marginBottom: '3px' }}>
                {selectedProvider.quickActionsTitle}
              </p>
              <p style={{ color: 'var(--od-muted, #717680)', fontSize: '12px', lineHeight: '1.5', marginBottom: '10px' }}>
                {selectedProvider.quickActionsDescription}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <a
                  href={selectedProvider.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...QUICK_ACTION_STYLE,
                    background: '#D97757',
                    border: '1px solid #C96445',
                    boxShadow: '0 1px 2px rgba(77, 35, 22, 0.18)',
                    color: '#FFFFFF'
                  }}
                >
                  {selectedProvider.link.label}
                  <LinkExternal01 width={14} height={14} aria-hidden="true" />
                </a>
                {selectedProvider.download && (
                  <a
                    href={selectedProvider.download.url}
                    download
                    style={{
                      ...QUICK_ACTION_STYLE,
                      background: 'var(--od-surface, #FFFFFF)',
                      border: '1px solid var(--od-border, #D5D7DA)',
                      boxShadow: '0 1px 2px rgba(10, 13, 18, 0.05)',
                      color: 'var(--od-text-2, #414651)'
                    }}
                  >
                    <Download01 width={15} height={15} aria-hidden="true" />
                    {selectedProvider.download.label}
                  </a>
                )}
              </div>
            </div>
          )}

          <div style={STEPS_CARD_STYLE}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--od-text-3, #535862)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {selectedProvider.manualInstructionsTitle || 'Setup'}
            </p>
            {selectedProvider.steps.map((step, index) => (
              <div
                key={`${selectedProvider.id}-step-${index}`}
                className="d-flex align-items-start"
                style={{ marginBottom: index < selectedProvider.steps.length - 1 ? '8px' : 0 }}
              >
                <div style={STEP_NUMBER_STYLE}>{index + 1}</div>
                <div style={{ color: 'var(--od-text-2, #414651)', fontSize: '13px', lineHeight: '1.5' }}>{step}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {selectedProvider.copyBlocks.map((block) => (
              <div key={`${selectedProvider.id}-${block.label}`}>
                <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '4px' }}>
                  <span style={{ color: 'var(--od-text-2, #414651)', fontWeight: 600, fontSize: '13px' }}>{block.label}</span>
                  <CopyButton label={block.label} value={block.value} onCopy={onCopy} />
                </div>
                <pre className="mb-0" style={CODE_BLOCK_STYLE}>
                  {block.value}
                </pre>
              </div>
            ))}
          </div>

          {selectedProvider.link && !selectedProvider.quickActionsTitle && (
            <div style={{ marginTop: '14px' }}>
              <a
                href={selectedProvider.link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '13px', fontWeight: 600, color: 'var(--od-accent, #1982C4)', textDecoration: 'none' }}
              >
                {selectedProvider.link.label} →
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ExistingAiPanel.propTypes = {
  existingAiIntegration: existingAiIntegrationPropType,
  onCopy: PropTypes.func.isRequired,
  onDismiss: PropTypes.func,
  onSetup: PropTypes.func,
  isMobileLayout: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'card'])
};

ExistingAiPanel.defaultProps = {
  isMobileLayout: false,
  variant: 'default'
};

export default ExistingAiPanel;
